import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Gavel,
  Hourglass,
  Mail,
  MailWarning,
  MailX,
  PauseCircle,
  UserX,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import type { StatusTone } from '@/components/ui/status-badge';

export interface DunningStatus {
  tone: StatusTone;
  icon: LucideIcon;
  label: MessageDescriptor;
}

export const DUNNING_STAGES = [
  'current',
  'reminder_1_sent',
  'reminder_2_sent',
  'reminder_3_sent',
  'final_notice_sent',
  'paid',
  'collections_handover',
  'cancelled',
] as const;
export type DunningStage = (typeof DUNNING_STAGES)[number];

/**
 * One status per dunning stage for the cockpit list, the drawer header and the
 * stage filter. The tone rises with the escalation; the label is translated
 * here because the API's `stage_label` is English only.
 */
export const DUNNING_STAGE_STATUS: Record<DunningStage, DunningStatus> = {
  current: { tone: 'neutral', icon: Circle, label: msg({ id: 'dunning.stage.current', message: 'Current' }) },
  reminder_1_sent: { tone: 'info', icon: Mail, label: msg({ id: 'dunning.stage.reminder_1_sent', message: 'Reminder 1 sent' }) },
  reminder_2_sent: { tone: 'warning', icon: Mail, label: msg({ id: 'dunning.stage.reminder_2_sent', message: 'Reminder 2 sent' }) },
  reminder_3_sent: { tone: 'warning', icon: MailWarning, label: msg({ id: 'dunning.stage.reminder_3_sent', message: 'Reminder 3 sent' }) },
  final_notice_sent: { tone: 'danger', icon: AlertTriangle, label: msg({ id: 'dunning.stage.final_notice_sent', message: 'Final notice' }) },
  paid: { tone: 'success', icon: CheckCircle2, label: msg({ id: 'dunning.stage.paid', message: 'Paid' }) },
  collections_handover: { tone: 'danger', icon: Gavel, label: msg({ id: 'dunning.stage.collections_handover', message: 'Handover' }) },
  cancelled: { tone: 'neutral', icon: XCircle, label: msg({ id: 'dunning.stage.cancelled', message: 'Cancelled' }) },
};

export function dunningStageStatus(stage: string | undefined | null): DunningStatus | undefined {
  return stage && stage in DUNNING_STAGE_STATUS ? DUNNING_STAGE_STATUS[stage as DunningStage] : undefined;
}

/** Why a reminder cannot go out right now. */
export const DUNNING_BLOCKER_STATUS: Record<string, DunningStatus> = {
  no_recipient: { tone: 'warning', icon: UserX, label: msg({ id: 'dunning.blocker.badge.noRecipient', message: 'Email missing' }) },
  send_failed: { tone: 'danger', icon: AlertCircle, label: msg({ id: 'dunning.blocker.badge.sendFailed', message: 'Send failed' }) },
  suppressed: { tone: 'neutral', icon: MailX, label: msg({ id: 'dunning.blocker.badge.suppressed', message: 'Suppressed' }) },
  awaiting_approval: { tone: 'warning', icon: Hourglass, label: msg({ id: 'dunning.blocker.awaitingApproval.badge', message: 'Awaiting approval' }) },
};

/** The run state of one household's dunning, next to its stage. */
export const DUNNING_RUN_STATUS = {
  paused: { tone: 'accent', icon: PauseCircle, label: msg({ id: 'dunning.cockpit.status.paused', message: 'Paused' }) },
  pausedIndefinitely: { tone: 'accent', icon: PauseCircle, label: msg({ id: 'dunning.cockpit.status.pausedIndefinitely', message: 'Paused indefinitely' }) },
  suppressed: { tone: 'warning', icon: MailX, label: msg({ id: 'dunning.cockpit.status.suppressed', message: 'Suppressed' }) },
  sendFailed: { tone: 'danger', icon: AlertCircle, label: msg({ id: 'dunning.cockpit.status.sendFailed', message: 'Send failed' }) },
  active: { tone: 'neutral', icon: Circle, label: msg({ id: 'dunning.cockpit.status.active', message: 'Active' }) },
} satisfies Record<string, DunningStatus>;
